import React, { useEffect, useState } from "react";
import { useOrganisations } from "../Auth/useOrganisations";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import GifLoader from "../Components/loader";
import {
  getClientsByOrganisation,
  createClient,
  updateClient,
  deleteClient,
  setDefaultClient,
  toggleBlockClient,
} from "../Services/ClientApi";
import ClientDetailModal from "../Components/Caisse/ClientDetailModal";

// ─────────────────────────────────────────────────────────────────────────────
// Empty form shape — no isDefault, no isActive (both system-managed)
// ─────────────────────────────────────────────────────────────────────────────
const EMPTY_FORM = {
  code: "",
  name: "",
  email: "",
  phone: "",
  address: "",
  taxNumber: "",
  note: "",
  code_erp: "",
};

// ─────────────────────────────────────────────────────────────────────────────
// Client Form Modal
// ─────────────────────────────────────────────────────────────────────────────
const ClientFormModal = ({
  isOpen,
  onClose,
  onSuccess,
  editingClient,
  organisationId,
}) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (editingClient) {
        setForm({
          code: editingClient.code || "",
          name: editingClient.name || "",
          email: editingClient.email || "",
          phone: editingClient.phone || "",
          address: editingClient.address || "",
          taxNumber: editingClient.taxNumber || "",
          note: editingClient.note || "",
          code_erp: editingClient.code_erp || "",
        });
      } else {
        setForm(EMPTY_FORM);
      }
    }
  }, [isOpen, editingClient]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.code.trim()) {
      toast.error(t("Le code client est obligatoire"));
      return;
    }
    if (!form.name.trim()) {
      toast.error(t("Le nom du client est obligatoire"));
      return;
    }

    setSaving(true);
    try {
      // isActive always true on create; never sent on edit (backend keeps current value)
      const payload = editingClient
        ? { ...form }
        : { ...form, organisation_id: organisationId, isActive: true };

      const response = editingClient
        ? await updateClient(editingClient.id, payload)
        : await createClient(payload);

      if (response.data.success) {
        toast.success(
          editingClient
            ? t("Client mis à jour avec succès")
            : t("Client créé avec succès"),
        );
        onSuccess();
        onClose();
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          t("Erreur lors de l'enregistrement du client"),
      );
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal fade show"
      style={{
        display: "block",
        backgroundColor: "rgba(0,0,0,0.5)",
        zIndex: 1050,
      }}
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          {/* Header */}
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="fa fa-user mr-2" />
              {editingClient ? t("Modifier le client") : t("Nouveau client")}
            </h5>
            <button type="button" className="close" onClick={onClose}>
              <span>&times;</span>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {/* Code + Nom */}
              <div className="row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label>
                      {t("Code")} <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.code}
                      onChange={(e) =>
                        handleChange("code", e.target.value.toUpperCase())
                      }
                      placeholder="EX: CLT001"
                      disabled={!!editingClient}
                    />
                    {editingClient && (
                      <small className="text-muted">
                        {t("Le code ne peut pas être modifié")}
                      </small>
                    )}
                  </div>
                </div>
                <div className="col-md-8">
                  <div className="form-group">
                    <label>
                      {t("Nom")} <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      placeholder={t("Nom complet du client")}
                    />
                  </div>
                </div>
              </div>

              {/* Email + Téléphone */}
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>{t("Email")}</label>
                    <input
                      type="email"
                      className="form-control"
                      value={form.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      placeholder="email@exemple.com"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>{t("Téléphone")}</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.phone}
                      onChange={(e) => handleChange("phone", e.target.value)}
                      placeholder="ex: 55 123 456"
                    />
                  </div>
                </div>
              </div>

              {/* Adresse */}
              <div className="form-group">
                <label>{t("Adresse")}</label>
                <input
                  type="text"
                  className="form-control"
                  value={form.address}
                  onChange={(e) => handleChange("address", e.target.value)}
                  placeholder={t("Adresse complète")}
                />
              </div>

              {/* Matricule fiscal + Code ERP */}
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>{t("Matricule fiscal")}</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.taxNumber}
                      onChange={(e) =>
                        handleChange("taxNumber", e.target.value)
                      }
                      placeholder="1234567X/A/M/000"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>{t("Code ERP")}</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.code_erp}
                      onChange={(e) => handleChange("code_erp", e.target.value)}
                      placeholder={t("Référence dans le système ERP")}
                    />
                  </div>
                </div>
              </div>

              {/* Note */}
              <div className="form-group">
                <label>{t("Note")}</label>
                <textarea
                  className="form-control"
                  rows="2"
                  value={form.note}
                  onChange={(e) => handleChange("note", e.target.value)}
                  placeholder={t("Remarques supplémentaires...")}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={saving}
              >
                {t("Annuler")}
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <i className="fa fa-spinner fa-spin mr-1" />
                    {t("Enregistrement...")}
                  </>
                ) : (
                  <>
                    <i className="fa fa-save mr-1" />
                    {editingClient ? t("Mettre à jour") : t("Enregistrer")}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Clients List
// ─────────────────────────────────────────────────────────────────────────────
const ClientsList = () => {
  const { t } = useTranslation();
  const [, , usedOrganisations] = useOrganisations();

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);

  // Draft filters (committed only on "Appliquer")
  const [filters, setFilters] = useState({ search: "", isBlocked: "" });
  const [appliedFilters, setAppliedFilters] = useState({
    search: "",
    isBlocked: "",
  });
  const [detailClient, setDetailClient] = useState(null);

  // ── Load ────────────────────────────────────────────────────────────────
  const loadClients = async () => {
    if (!usedOrganisations) return;
    try {
      setLoading(true);
      const response = await getClientsByOrganisation(usedOrganisations);
      if (response.data.success) {
        setClients(response.data.data || []);
      }
    } catch (error) {
      toast.error(t("Erreur lors du chargement des clients"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClients();
  }, [usedOrganisations]);

  // ── Client-side filtering ───────────────────────────────────────────────
  const filteredClients = clients.filter((c) => {
    const search = appliedFilters.search.toLowerCase();
    if (
      search &&
      !c.name?.toLowerCase().includes(search) &&
      !c.code?.toLowerCase().includes(search) &&
      !c.email?.toLowerCase().includes(search) &&
      !c.phone?.toLowerCase().includes(search)
    )
      return false;

    if (appliedFilters.isBlocked === "oui" && !c.isBlocked) return false;
    if (appliedFilters.isBlocked === "non" && c.isBlocked) return false;

    return true;
  });

  const handleFilterChange = (field, value) =>
    setFilters((prev) => ({ ...prev, [field]: value }));

  const handleApplyFilters = () => setAppliedFilters({ ...filters });

  const handleResetFilters = () => {
    const empty = { search: "", isBlocked: "" };
    setFilters(empty);
    setAppliedFilters(empty);
  };

  // ── CRUD ────────────────────────────────────────────────────────────────
  const handleOpenCreate = () => {
    setEditingClient(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (client) => {
    // Default client row is read-only — edit button is disabled for it
    setEditingClient(client);
    setIsModalOpen(true);
  };

  const handleDelete = async (client) => {
    const result = await Swal.fire({
      title: t("Confirmation"),
      text: t(`Supprimer le client "${client.name}" ?`),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("Oui, supprimer"),
      cancelButtonText: t("Annuler"),
      reverseButtons: true,
    });
    if (!result.isConfirmed) return;

    try {
      const response = await deleteClient(client.id);
      if (response.data.success) {
        toast.success(t("Client supprimé avec succès"));
        loadClients();
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || t("Erreur lors de la suppression"),
      );
    }
  };

  const handleToggleBlock = async (client) => {
    const isCurrentlyBlocked = client.isBlocked;
    const result = await Swal.fire({
      title: isCurrentlyBlocked
        ? t("Débloquer le client")
        : t("Bloquer le client"),
      text: isCurrentlyBlocked
        ? t(`Débloquer "${client.name}" ?`)
        : t(`Bloquer "${client.name}" ? Il ne pourra plus être utilisé.`),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: isCurrentlyBlocked ? "#28a745" : "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: isCurrentlyBlocked
        ? t("Oui, débloquer")
        : t("Oui, bloquer"),
      cancelButtonText: t("Annuler"),
      reverseButtons: true,
    });
    if (!result.isConfirmed) return;

    try {
      const response = await toggleBlockClient(client.id);
      if (response.data.success) {
        toast.success(
          isCurrentlyBlocked
            ? t("Client débloqué avec succès")
            : t("Client bloqué avec succès"),
        );
        loadClients();
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || t("Erreur lors de l'opération"),
      );
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="container-fluid">
      <GifLoader loading={loading} />

      <div className="row">
        <div className="col-12">
          <div className="box">
            <div className="box-body">
              {/* Header */}
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="box-title mb-0">
                  <i className="fa fa-users mr-2" />
                  {t("Clients")}
                </h4>
                <button className="btn btn-primary" onClick={handleOpenCreate}>
                  <i className="fa fa-plus mr-2" />
                  {t("Nouveau client")}
                </button>
              </div>

              {/* Filter Card */}
              <div className="card mb-4">
                <div className="card-body">
                  <div className="row align-items-end">
                    {/* Search */}
                    <div className="col-md-7 mb-2">
                      <div className="form-group mb-0">
                        <label>{t("Recherche")}</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder={t("Nom, code, email, téléphone...")}
                          value={filters.search}
                          onChange={(e) =>
                            handleFilterChange("search", e.target.value)
                          }
                          onKeyDown={(e) =>
                            e.key === "Enter" && handleApplyFilters()
                          }
                        />
                      </div>
                    </div>

                    {/* Bloqué */}
                    <div className="col-md-3 mb-2">
                      <div className="form-group mb-0">
                        <label>{t("Statut")}</label>
                        <select
                          className="form-control"
                          value={filters.isBlocked}
                          onChange={(e) =>
                            handleFilterChange("isBlocked", e.target.value)
                          }
                        >
                          <option value="">{t("Tous")}</option>
                          <option value="non">{t("Actif")}</option>
                          <option value="oui">{t("Bloqué")}</option>
                        </select>
                      </div>
                    </div>

                    {/* Action buttons — kept inline with the filters, not on their own row */}
                    <div className="col-md-2 mb-2">
                      <div className="form-group mb-0">
                        <label>&nbsp;</label>
                        <div className="d-flex" style={{ gap: "8px" }}>
                          <button
                            className="btn btn-primary"
                            onClick={handleApplyFilters}
                            title={t("Rechercher")}
                          >
                            <i className="fa fa-search" />
                          </button>
                          <button
                            className="btn btn-outline-secondary"
                            onClick={handleResetFilters}
                            title={t("Réinitialiser")}
                          >
                            <i className="fa fa-undo" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-12 text-right">
                      <span className="text-muted" style={{ fontSize: "13px" }}>
                        {filteredClients.length} {t("client(s) trouvé(s)")}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="table-responsive">
                <table className="table table-hover table-bordered mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th>{t("Code")}</th>
                      <th>{t("Nom")}</th>
                      <th>{t("Email")}</th>
                      <th>{t("Téléphone")}</th>
                      <th>{t("Adresse")}</th>
                      <th>{t("Matricule fiscal")}</th>
                      <th className="text-right">{t("Crédit")}</th>
                      <th className="text-center">{t("Statut")}</th>
                      <th className="text-center">{t("Actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredClients.length > 0 ? (
                      filteredClients.map((client) => {
                        const isDefault = client.isDefault;
                        return (
                          <tr
                            key={client.id}
                            style={{
                              opacity: client.isBlocked ? 0.6 : 1,
                              backgroundColor: isDefault
                                ? "#f0f4ff"
                                : undefined,
                            }}
                          >
                            {/* Code */}
                            <td>
                              <span className="badge badge-pill badge-light font-weight-600">
                                {client.code}
                              </span>
                              {isDefault && (
                                <span
                                  className="badge badge-primary ml-1"
                                  style={{ fontSize: "10px" }}
                                >
                                  <i className="fa fa-lock mr-1" />
                                  {t("Système")}
                                </span>
                              )}
                            </td>

                            {/* Nom */}
                            <td className="font-weight-600">
                              {client.name}
                            </td>

                            {/* Email */}
                            <td>
                              {client.email ? (
                                <a
                                  href={`mailto:${client.email}`}
                                  className="text-primary"
                                >
                                  {client.email}
                                </a>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>

                            {/* Téléphone */}
                            <td>
                              {client.phone || (
                                <span className="text-muted">—</span>
                              )}
                            </td>

                            {/* Adresse */}
                            <td>
                              {client.address || (
                                <span className="text-muted">—</span>
                              )}
                            </td>

                            {/* Matricule */}
                            <td>
                              {client.taxNumber || (
                                <span className="text-muted">—</span>
                              )}
                            </td>

                            {/* Crédit */}
                            <td className="text-right">
                              {client.credit > 0 ? (
                                <span style={{ color: "#fd683e", fontWeight: 600 }}>
                                  {client.credit.toFixed(3)} DT
                                </span>
                              ) : (
                                <span className="text-muted">0.000 DT</span>
                              )}
                            </td>

                            {/* Statut */}
                            <td className="text-center">
                              {isDefault ? (
                                <span className="badge badge-primary">
                                  {t("Par défaut")}
                                </span>
                              ) : client.isBlocked ? (
                                <span className="badge badge-danger">
                                  <i className="fa fa-ban mr-1" />
                                  {t("Bloqué")}
                                </span>
                              ) : (
                                <span className="badge badge-success">
                                  {t("Actif")}
                                </span>
                              )}
                            </td>

                            {/* Actions */}
                            <td className="text-center">
                              <div className="d-flex justify-content-center" style={{ gap: "4px" }}>
                                {/* View details */}
                                <button
                                  className="btn btn-sm btn-info"
                                  title={t("Voir détails")}
                                  onClick={() => setDetailClient(client.id || client._id)}
                                >
                                  <i className="fa fa-eye" />
                                </button>

                                {!isDefault && (
                                  <>
                                    {/* Edit */}
                                    <button
                                      className="btn btn-sm btn-warning"
                                      title={t("Modifier")}
                                      onClick={() => handleOpenEdit(client)}
                                      disabled={client.isBlocked}
                                    >
                                      <i className="fa fa-edit" />
                                    </button>

                                    {/* Block / Unblock */}
                                    <button
                                      className={`btn btn-sm ${client.isBlocked ? "btn-success" : "btn-secondary"}`}
                                      title={client.isBlocked ? t("Débloquer") : t("Bloquer")}
                                      onClick={() => handleToggleBlock(client)}
                                    >
                                      <i className={`fa ${client.isBlocked ? "fa-unlock" : "fa-ban"}`} />
                                    </button>

                                    {/* Delete */}
                                    <button
                                      className="btn btn-sm btn-danger"
                                      title={t("Supprimer")}
                                      onClick={() => handleDelete(client)}
                                      disabled={client.isBlocked}
                                    >
                                      <i className="fa fa-trash" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="9" className="text-center py-4">
                          {loading
                            ? t("Chargement...")
                            : t("Aucun client trouvé")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      <ClientFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingClient(null);
        }}
        onSuccess={loadClients}
        editingClient={editingClient}
        organisationId={usedOrganisations}
      />
      <ClientDetailModal
        open={!!detailClient}
        onClose={() => setDetailClient(null)}
        clientId={detailClient}
      />
    </div>
  );
};

export default ClientsList;
